import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';

/** یک شکل از آیکون؛ همان ویژگی‌های SVG که Lucide به کار می‌برد */
interface IconNode {
  tag: 'path' | 'line' | 'rect' | 'circle' | 'polyline' | 'polygon';
  d?: string;
  x?: string;
  y?: string;
  x1?: string;
  x2?: string;
  y1?: string;
  y2?: string;
  width?: string;
  height?: string;
  rx?: string;
  ry?: string;
  cx?: string;
  cy?: string;
  r?: string;
  points?: string;
}

/**
 * آیکون‌های خطی Lucide (نسخه‌ی 0.474، مجوز ISC) — همان مجموعه‌ای که طرح صفحه‌ی
 * اصلی با آن ساخته شده. به‌جای کل کتابخانه (۳۶۰ کیلوبایت) فقط همین چند آیکون
 * این‌جا نگه داشته شده و با خود قالب انگولار به SVG تبدیل می‌شود؛ پس در خروجی
 * SSR هم هست، درخواست جداگانه‌ای ندارد و به innerHTML (که DOM سمت سرور روی
 * SVG پشتیبانی نمی‌کند) نیازی نیست.
 */
const ICONS = {
  'scale': [{tag: 'path', d: 'm16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z'}, {tag: 'path', d: 'm2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z'}, {tag: 'path', d: 'M7 21h10'}, {tag: 'path', d: 'M12 3v18'}, {tag: 'path', d: 'M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2'}],
  'landmark': [{tag: 'line', x1: '3', x2: '21', y1: '22', y2: '22'}, {tag: 'line', x1: '6', x2: '6', y1: '18', y2: '11'}, {tag: 'line', x1: '10', x2: '10', y1: '18', y2: '11'}, {tag: 'line', x1: '14', x2: '14', y1: '18', y2: '11'}, {tag: 'line', x1: '18', x2: '18', y1: '18', y2: '11'}, {tag: 'polygon', points: '12 2 20 7 4 7'}],
  'badge-check': [{tag: 'path', d: 'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z'}, {tag: 'path', d: 'm9 12 2 2 4-4'}],
  'credit-card': [{tag: 'rect', width: '20', height: '14', x: '2', y: '5', rx: '2'}, {tag: 'line', x1: '2', x2: '22', y1: '10', y2: '10'}],
  'shield-check': [{tag: 'path', d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z'}, {tag: 'path', d: 'm9 12 2 2 4-4'}],
  'trending-up': [{tag: 'polyline', points: '22 7 13.5 15.5 8.5 10.5 2 17'}, {tag: 'polyline', points: '16 7 22 7 22 13'}],
  'file-text': [{tag: 'path', d: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z'}, {tag: 'path', d: 'M14 2v4a2 2 0 0 0 2 2h4'}, {tag: 'path', d: 'M10 9H8'}, {tag: 'path', d: 'M16 13H8'}, {tag: 'path', d: 'M16 17H8'}],
  'banknote': [{tag: 'rect', width: '20', height: '12', x: '2', y: '6', rx: '2'}, {tag: 'circle', cx: '12', cy: '12', r: '2'}, {tag: 'path', d: 'M6 12h.01M18 12h.01'}],
  'smartphone': [{tag: 'rect', width: '14', height: '20', x: '5', y: '2', rx: '2', ry: '2'}, {tag: 'path', d: 'M12 18h.01'}],
  'user-check': [{tag: 'path', d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'}, {tag: 'circle', cx: '9', cy: '7', r: '4'}, {tag: 'polyline', points: '16 11 18 13 22 9'}],
  'coins': [{tag: 'circle', cx: '8', cy: '8', r: '6'}, {tag: 'path', d: 'M18.09 10.37A6 6 0 1 1 10.34 18'}, {tag: 'path', d: 'M7 6h1v4'}, {tag: 'path', d: 'm16.71 13.88.7.71-2.82 2.82'}],
  'shopping-bag': [{tag: 'path', d: 'M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z'}, {tag: 'path', d: 'M3 6h18'}, {tag: 'path', d: 'M16 10a4 4 0 0 1-8 0'}],
  'apple': [{tag: 'path', d: 'M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06Z'}, {tag: 'path', d: 'M10 2c1 .5 2 2 2 5'}],
  'chevron-left': [{tag: 'path', d: 'm15 18-6-6 6-6'}],
  'chevron-right': [{tag: 'path', d: 'm9 18 6-6-6-6'}],
} satisfies Record<string, IconNode[]>;

export type IconName = keyof typeof ICONS;

@Component({
  selector: 'app-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"
         [attr.width]="size()" [attr.height]="size()" [attr.stroke-width]="strokeWidth()">
      @for (n of nodes(); track $index) {
        @switch (n.tag) {
          @case ('path') {
            <svg:path [attr.d]="n.d"/>
          }
          @case ('line') {
            <svg:line [attr.x1]="n.x1" [attr.x2]="n.x2" [attr.y1]="n.y1" [attr.y2]="n.y2"/>
          }
          @case ('rect') {
            <svg:rect [attr.x]="n.x" [attr.y]="n.y" [attr.width]="n.width" [attr.height]="n.height"
                      [attr.rx]="n.rx" [attr.ry]="n.ry"/>
          }
          @case ('circle') {
            <svg:circle [attr.cx]="n.cx" [attr.cy]="n.cy" [attr.r]="n.r"/>
          }
          @case ('polyline') {
            <svg:polyline [attr.points]="n.points"/>
          }
          @case ('polygon') {
            <svg:polygon [attr.points]="n.points"/>
          }
        }
      }
    </svg>
  `,
  styles: `:host { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
           svg { display: block; }`
})
export class LucideIconComponent {
  readonly name = input.required<IconName>();
  readonly size = input(20);
  // پیش‌فرض طرح (کامپوننت Icon در سیستم طراحی بازر)
  readonly strokeWidth = input(1.75);

  readonly nodes = computed<IconNode[]>(() => ICONS[this.name()]);
}
